import i18n from '@/lang'

// export const mentionRegex = /@([\w\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FFF\uFF66-\uFF9Fー\s]*)$/
export const mentionRegex = /[@\uFF20]([\w\uFF10-\uFF19\uFF21-\uFF3A\uFF41-\uFF5A\u3000\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FFF\uFF66-\uFF9Fー\s]*)$/

/**
 * NWS-1773: メンション候補ポップアップに紐づく後始末の関数。
 *
 * ポップアップは v-show で非表示にするだけで body から外れないため、
 * 「body から消えたら解放する」という従来の解放条件が成立せず、
 * resize リスナと MutationObserver が呼び出しのたびに積み上がっていた。
 * MutationObserver は body 全体を subtree で監視しているので、
 * 積み上がった数だけ「編集中の1キー入力」でコールバックが走り、
 * 変換の待ち時間が使うほど伸びていく。
 *
 * ここで直前のインスタンスを保持し、新規登録の前に必ず解放することで
 * 同時に生存するインスタンスを常に1個に保つ。
 */
let activeMentionPopupCleanup: (() => void) | null = null

/**
 * 現在生存しているメンション候補ポップアップの監視を解放する。
 * ポップアップを閉じるとき・画面を破棄するときに呼ぶ。
 * 未登録の状態で呼んでも安全。
 */
export const releaseMentionPopup = () => {
  if (activeMentionPopupCleanup) {
    activeMentionPopupCleanup()
    activeMentionPopupCleanup = null
  }
}

export const calculateMentionPopup = ({
  suggestionDiv,
  getRect,
  callback,
  isInModal,
  editorIframe
}: {
  suggestionDiv: HTMLElement
  getRect: () => DOMRect
  callback: () => void
  isInModal?: boolean
  editorIframe: HTMLElement
}) => {
  let isPopupPositioned = false

  const setPosition = () => {
    const currentIframeRect = editorIframe.getBoundingClientRect()
    const rect = getRect()

    const scrollOffset = isInModal
      ? 0
      : window.scrollY || document.documentElement.scrollTop

    suggestionDiv.style.top = `${rect.bottom +
      currentIframeRect.top +
      +scrollOffset}px`
    suggestionDiv.style.left = `${rect.left + currentIframeRect.left}px`
  }

  if (!isPopupPositioned) {
    setPosition()
    isPopupPositioned = true
  }

  suggestionDiv.style.position = isInModal ? 'fixed' : 'absolute'
  if (isInModal) {
    suggestionDiv.style.zIndex = '9999'
  }
  callback()

  // 前回分が残っていれば必ず解放してから登録する（同時に1個だけ生かす）
  releaseMentionPopup()

  let timer: number | null = null
  const debounceUpdate = () => {
    if (timer !== null) {
      window.clearTimeout(timer)
    }
    timer = window.setTimeout(setPosition, 50)
  }

  window.addEventListener('resize', debounceUpdate)

  let observer: MutationObserver | null = null

  const cleanup = () => {
    if (timer !== null) {
      window.clearTimeout(timer)
      timer = null
    }
    window.removeEventListener('resize', debounceUpdate)
    if (observer) {
      observer.disconnect()
      observer = null
    }
  }

  // 従来の解放条件は残す（body から外す使い方をした場合の保険）。
  // 通常はここではなく releaseMentionPopup() 側で解放される。
  observer = new MutationObserver(() => {
    if (!document.body.contains(suggestionDiv)) {
      cleanup()
      if (activeMentionPopupCleanup === cleanup) {
        activeMentionPopupCleanup = null
      }
    }
  })

  observer.observe(document.body, { childList: true, subtree: true })

  activeMentionPopupCleanup = cleanup
}

export const handleSelectSuggestion = ({
  suggestion,
  editorInstance,
  callback,
  insertFromOutside = false
}: {
  editorInstance: any
  suggestion: any
  callback?: () => void
  insertFromOutside?: boolean
}) => {
  if (!editorInstance) return
  const selection = editorInstance.selection
  if (!selection) return

  const createMentionWrapper = () => {
    const wrapper = document.createElement('span')
    wrapper.setAttribute('data-at-embedded', '')
    wrapper.setAttribute('contenteditable', 'false')

    // space trước
    wrapper.appendChild(document.createTextNode(' '))

    // mention node
    const mentionNode = document.createElement('span')
    mentionNode.className = 'mention'
    mentionNode.setAttribute('data-id', suggestion.id.toString())
    mentionNode.setAttribute('translate', 'no')
    mentionNode.setAttribute('style', 'color:#007bff; font-weight:bold;')
    mentionNode.textContent = `@${suggestion.name}`
    wrapper.appendChild(mentionNode)

    // space sau
    wrapper.appendChild(document.createTextNode(' '))

    return wrapper
  }

  if (insertFromOutside) {
    editorInstance.focus()
    selection.collapse()

    const wrapper = createMentionWrapper()
    editorInstance.selection.setNode(wrapper)

    // separator node
    const separator = document.createTextNode('')
    wrapper.parentNode?.insertBefore(separator, wrapper.nextSibling)

    // caret sau separator
    const rng = editorInstance.dom.createRng()
    rng.setStartAfter(separator)
    rng.collapse(true)
    editorInstance.selection.setRng(rng)

    callback?.()
    return
  }

  const rng = selection.getRng()
  const container = rng.startContainer
  const offset = rng.startOffset

  if (container.nodeType === Node.TEXT_NODE) {
    const text = container.textContent || ''
    const beforeCaretText = text.slice(0, offset)
    const match = beforeCaretText.match(mentionRegex)

    if (match) {
      const mentionText = match[0].length
      const mentionStart = offset - mentionText
      const mentionEnd = offset

      // tạo range cho phần trigger
      const newRange = editorInstance.dom.createRng()
      newRange.setStart(container, mentionStart)
      newRange.setEnd(container, mentionEnd)
      selection.setRng(newRange)

      // insert mention wrapper
      const wrapper = createMentionWrapper()
      editorInstance.insertContent(wrapper.outerHTML)

      // thêm separator
      const parent = container.parentNode
      if (parent) {
        const insertedNode = parent.querySelector(
          'span[data-at-embedded]:last-child'
        )
        if (insertedNode) {
          const separator = document.createTextNode('')
          insertedNode.parentNode?.insertBefore(
            separator,
            insertedNode.nextSibling
          )

          // đặt caret sau separator
          const rng2 = editorInstance.dom.createRng()
          rng2.setStartAfter(separator)
          rng2.collapse(true)
          editorInstance.selection.setRng(rng2)
        }
      }

      callback?.()
    }
  }
}

export const handleDeleteSuggestion = ({
  event,
  editorInstance,
  callback
}: {
  event: any
  editorInstance: any
  callback: () => void
}) => {
  const rng = editorInstance.selection.getRng()
  const container = rng.startContainer
  const offset = rng.startOffset

  if (container.nodeType === Node.TEXT_NODE) {
    const text = container.textContent || ''
    const isAfterZWSP = text.charCodeAt(offset - 1) === 0xfeff

    if (isAfterZWSP) {
      const mention = container.previousSibling

      if (
        mention &&
        mention.nodeType === Node.ELEMENT_NODE &&
        mention.classList.contains('mention')
      ) {
        const prevTextNode = mention.previousSibling
        const shouldInsertSpace =
          prevTextNode &&
          prevTextNode.nodeType === Node.TEXT_NODE &&
          prevTextNode.textContent?.endsWith(' ')

        mention.remove()
        container.textContent = text.slice(1)
        event.preventDefault()

        if (shouldInsertSpace) {
          editorInstance.execCommand('InsertText', false, ' ')
        }

        editorInstance.focus()
        callback()
      }
    }
  }
}

export const handleInputToMention = ({
  editorInstance,
  errorCallback,
  successCallback
}: {
  editorInstance: any
  errorCallback: () => void
  successCallback: (keyword: string) => void
}) => {
  const selection = editorInstance.selection
  const rng = selection.getRng()
  const container = rng.startContainer
  const offset = rng.startOffset

  if (container.nodeType !== Node.TEXT_NODE) {
    errorCallback()
    return
  }

  const text = container.textContent || ''
  const beforeCaretText = text.slice(0, offset)

  if (!beforeCaretText.includes('@') && !beforeCaretText.includes('＠')) {
    errorCallback()
    return
  }

  const match = beforeCaretText.match(mentionRegex)

  if (!match) {
    errorCallback()
    return
  }

  const keyword = match[1]
  successCallback(keyword)
}

export const getMentionIdsFromContent = (content: string) => {
  if (content) {
    const regex = /data-id="(-?\d+)"/g
    const mentionIds = [...content.matchAll(regex)].map(m => m[1])
    const uniqueMentionsIds = [...new Set(mentionIds)]
    return uniqueMentionsIds
  }
  return []
}

export enum MENTION_VALUE {
  ALL = -1,
  LOADING = -2
}

export const allItemMention = {
  id: MENTION_VALUE.ALL,
  name: 'All',
  avatar: ''
}

export const loadingItemMention = {
  id: MENTION_VALUE.LOADING,
  name: i18n.t('common.message.searching'),
  avatar: ''
}

export const allMentionCheckKeyword = ['a', 'al', 'all']

export function getCaretTextBefore(el: HTMLElement): string {
  const sel = window.getSelection()
  if (!sel || sel.rangeCount === 0) return ''

  const range = sel.getRangeAt(0).cloneRange()
  const preRange = range.cloneRange()

  preRange.selectNodeContents(el)
  preRange.setEnd(range.endContainer, range.endOffset)

  const fragment = preRange.cloneContents()

  fragment.querySelectorAll('.mention').forEach(n => n.remove())

  return fragment.textContent || ''
}

export function filterMentions(htmlString: string, allowedIds: number[]) {
  const parser = new DOMParser()
  const doc = parser.parseFromString(htmlString, 'text/html')

  const mentionWrappers = doc.querySelectorAll('span[data-at-embedded]')

  mentionWrappers.forEach(wrapper => {
    const mentionSpan = wrapper.querySelector('.mention') as HTMLElement | null
    if (!mentionSpan) return

    const currentId = parseInt(mentionSpan.getAttribute('data-id') || '', 10)

    if (
      currentId === MENTION_VALUE.ALL ||
      currentId === MENTION_VALUE.LOADING
    ) {
      return
    }

    if (!allowedIds.includes(currentId)) {
      wrapper.remove()
    }
  })

  return doc.body.innerHTML
}
